<template>
  <div
    ref="banner"
    class="base-banner"
    :class="[variant]"
    :data-qa="dataQa"
  >
    <div
      v-if="title || hasSlot['title']"
      class="base-banner__title"
    >
      <slot name="title">
        <p class="kds-typography-title-medium">{{ title }}</p>
      </slot>
    </div>
    <div
      v-if="description || hasSlot['default']"
      class="base-banner__description"
    >
      <slot>
        <p class="kds-typography-paragraph-medium">{{ description }}</p>
      </slot>
    </div>
    <div
      v-if="hasSlot['actions']"
      class="base-banner__actions"
    >
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import useVueSlots from '@shared/composables/useVueSlots.js'

const { hasSlot } = useVueSlots(['default', 'actions', 'title'])
const props = defineProps({
  variant: {
    type: String,
    default: 'success',
    validator: (val) =>
      ['success', 'warning', 'important', 'info', 'neutral'].includes(val),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  dataQa: {
    type: String,
    default: '',
  },
  scrollIntoView: {
    type: Boolean,
    default: false,
  },
})

const banner = ref(null)

onMounted(() => {
  if (props.scrollIntoView) {
    banner.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
})
</script>

<!-- WARN: Do not put 'scoped' here. Adding scoped prevents the use of the CSS variables in other locations. -->
<style lang="scss">
@use 'sass:map';

.base-banner {
  background: $kds-color-background-green-subtle;
  color: $kds-color-text-primary;
  border-radius: $kds-space-2x;
  padding: $kds-space-3x;
  @include flex-container(column, flex-start, sm);

  &.neutral {
    background: $kds-color-background-subtle;
  }

  &.info {
    background: $kds-color-background-blue-subtle;
  }

  &.important {
    background: $kds-color-background-yellow-subtle;
  }

  &.warning {
    background: $kds-color-background-magenta-subtle;
  }

  &__title {
    display: flex;
    justify-content: flex-start;
  }

  &__actions {
    @include flex-container(column, flex-start, sm, flex-start);
  }
}
</style>
